import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Mariposa from "@/components/Mariposa";
import GemBadge from "@/components/GemBadge";
import { TIER_META, normalizeTier } from "@/constants";
import { Download, Copy, Check, Share2 } from "lucide-react";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSeal() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${PUBLIC_API}/public/supplier-profile/${id}`)
      .then((r) => setProfile(r.data))
      .catch(() => setError("Este sello no existe o ya no es válido."));
  }, [id]);

  const downloadSeal = (format) => {
    const url = `${PUBLIC_API}/public/seal/${id}?format=${format}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `sello-lala-elite-${id?.slice(0, 8)}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyEmbed = async () => {
    const embedUrl = `${PUBLIC_API}/public/seal/${id}?format=svg`;
    const html = `<a href="${window.location.origin}/sello/${id}" target="_blank" rel="noopener">
  <img src="${embedUrl}" alt="Sello Lala Elite" style="max-width: 300px; height: auto;" />
</a>`;
    try {
      await navigator.clipboard.writeText(html);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      window.prompt("Copia el código HTML:", html);
    }
  };

  const sharePage = async () => {
    const url = `${window.location.origin}/sello/${id}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Sello Lala Elite", url }); } catch (e) { /* user cancel */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch (e) { window.prompt("Copia este enlace:", url); }
    }
  };

  if (error) {
    return (
      <div className="min-h-screen paper flex items-center justify-center px-6">
        <div className="text-center max-w-md" data-testid="public-seal-error">
          <h1 className="font-display text-3xl mb-2">Sello no encontrado</h1>
          <p className="text-[var(--ink-soft)]">{error}</p>
          <Link to="/" className="btn-primary mt-6 inline-flex">Ir a Lala Elite</Link>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen paper flex items-center justify-center">
        <div className="font-hand text-2xl text-[var(--lala-blue)]">Cargando sello…</div>
      </div>
    );
  }

  const meta = TIER_META[normalizeTier(profile.tier)] || TIER_META.commercial;
  const tierColor = meta.color;

  return (
    <div className="min-h-screen paper" data-testid="public-seal-page">
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <header className="text-center mb-10">
          <Link to="/" className="font-display text-2xl text-[var(--lala-blue)] hover:underline">
            Lala Elite
          </Link>
          <div className="font-hand text-lg text-[var(--ink-soft)] -mt-1">construido nivel a nivel, sin atajos</div>
        </header>

        {/* Seal card */}
        <div className="sketch-border bg-[var(--cream-soft)] p-10 max-w-2xl mx-auto rise relative" data-testid="seal-card">
          {profile.is_best_of_category && (
            <div
              data-testid="seal-best-of-category-badge"
              className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 shadow-md"
              style={{ background: "linear-gradient(135deg, #FFC400 0%, #E0A800 100%)", color: "#11142B" }}
              title="Distintivo otorgado al mejor de su categoría"
            >
              ★ {profile.best_of_category_label || "Mejor de su Categoría"}
            </div>
          )}
          <div className="text-center">
            <div className="font-hand text-xl text-[var(--lala-blue)]">distintivo Lala Elite</div>
            <div className="my-6 flex justify-center">
              <Mariposa size={180} animated palette="lala" />
            </div>
            <div className="font-display text-5xl text-[var(--ink)]" data-testid="seal-tier">{meta.label}</div>
            <div className="mt-2 flex justify-center">
              <GemBadge tier={normalizeTier(profile.tier)} size="md" variant="pill" />
            </div>
            <div className="font-hand text-2xl mt-1" style={{ color: tierColor }} data-testid="seal-tagline">
              {meta.tagline}
            </div>
            <div className="mt-6 border-t border-[var(--line)] pt-6">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">otorgado a</div>
              <h1 className="font-display text-3xl mt-1" data-testid="seal-supplier-name">{profile.name}</h1>
              {profile.category && (
                <div className="text-sm text-[var(--ink-soft)] mt-1">{profile.category}</div>
              )}
            </div>
            <div className="mt-6 text-xs text-[var(--ink-soft)]" data-testid="seal-verify">
              Período {profile.period || "—"} · Código de verificación <b className="font-mono">{profile.verify_code}</b>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 max-w-2xl mx-auto grid sm:grid-cols-2 gap-3" data-testid="seal-actions">
          <button onClick={() => downloadSeal("png")}
            data-testid="download-png-btn"
            className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90">
            <Download size={16} /> Descargar PNG
          </button>
          <button onClick={() => downloadSeal("svg")}
            data-testid="download-svg-btn"
            className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[var(--cream-soft)]">
            <Download size={16} /> Descargar SVG
          </button>
          <button onClick={copyEmbed}
            data-testid="copy-embed-btn"
            className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[var(--cream-soft)]">
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? "¡Copiado!" : "Copiar HTML embed"}
          </button>
          <button onClick={sharePage}
            data-testid="share-btn"
            className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[var(--cream-soft)]">
            <Share2 size={16} /> Compartir
          </button>
        </div>

        {/* About */}
        <div className="mt-10 max-w-2xl mx-auto text-center text-sm text-[var(--ink-soft)]">
          Este sello reconoce a un proveedor de la familia <b>LALA Elite</b> — una comunidad que
          comparte estándares, confianza y un mismo camino de crecimiento en calidad, costo, continuidad,
          innovación, sustentabilidad y relación.
          <Link to="/" className="text-[var(--lala-blue)] ml-1 hover:underline">Conoce el programa →</Link>
        </div>
      </main>

      <footer className="mt-12 border-t border-[var(--line)] bg-[var(--cream-soft)] py-6 text-center text-xs text-[var(--ink-soft)]">
        Lala Elite · Un programa de Grupo Lala · MMXXVI
      </footer>
    </div>
  );
}
